import { Waves, Dumbbell, UtensilsCrossed, Users } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';

const amenities = [
  {
    icon: Waves,
    title: 'Signature Lagoon',
    description: 'Crystal-clear swimming lagoon with beach entry, cabanas, and sunset views.',
  },
  {
    icon: Dumbbell,
    title: 'Fitness Center',
    description: 'State-of-the-art gym with modern equipment and wellness studio.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Dining & Food Hall',
    description: '8 curated kitchen concepts, outdoor bar, and flexible event space.',
  },
  {
    icon: Users,
    title: 'Family Recreation',
    description: 'Landscaped play areas, walking paths, and community gathering spaces.',
  },
];

export const LagoonAmenities = () => {
  const navigate = useNavigate();

  return (
    <section id="amenities" className="py-20 lg:py-28 bg-gradient-to-b from-background to-soft-cream/30 scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Lagoon Section */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-foreground mb-6">
                The Lagoon
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                A signature lagoon and pool form the community's social heart—swim, relax, and meet after work.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Designed as the centerpiece of The Pearl, the lagoon offers a unique Caribbean lifestyle experience 
                with white sand beaches, shallow wading areas for families, and deeper sections for swimming. 
                Surrounded by lush tropical landscaping and comfortable lounging areas.
              </p>
            </div>
            <div className="order-1 lg:order-2 rounded-2xl overflow-hidden shadow-luxury">
              <img
                src="/img/lagoon-amenity.jpg"
                alt="The Pearl lagoon and pool amenities"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
          </div>
        </div>

        {/* Food Hall Section */}
        <div className="mb-24">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="rounded-2xl overflow-hidden shadow-luxury">
              <img
                src="/img/food-hall.jpg"
                alt="The Pearl food hall interior"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
            </div>
            <div>
              <h2 className="font-display text-3xl lg:text-5xl font-semibold text-foreground mb-6">
                Food Hall
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Indoor food hall with eight kitchens (concepts in development), plus an outdoor bar and flexible event space.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-6">
                From morning coffee to evening cocktails, the food hall will be your neighborhood destination. 
                Multiple cuisine concepts ensure variety for every taste, while the outdoor terrace offers 
                the perfect spot for weekend gatherings and community events.
              </p>
              <a
                href="/commercial"
                onClick={(e) => {
                  e.preventDefault();
                  trackEvent('internal_link_click', { from: 'lagoon_amenities', to: '/commercial' });
                  navigate('/commercial');
                }}
                className="text-brand-teal hover:text-brand-teal/80 underline text-sm font-medium transition-colors inline-block"
              >
                Learn about commercial leasing opportunities →
              </a>
            </div>
          </div>
        </div>

        {/* Amenities Grid */}
        <div>
          <h3 className="font-display text-2xl lg:text-3xl font-semibold text-center text-foreground mb-16">
            Lifestyle Amenities
          </h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {amenities.map((amenity) => {
              const Icon = amenity.icon;
              return (
                <Card key={amenity.title} className="border-none shadow-soft group hover:shadow-luxury hover:-translate-y-2 transition-all duration-300">
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-brand-teal/10 mb-4 group-hover:bg-brand-teal/20 group-hover:scale-110 transition-all duration-300">
                      <Icon className="w-7 h-7 text-brand-teal group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    <h4 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-brand-teal transition-colors duration-300">
                      {amenity.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {amenity.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="mt-12 text-center">
            <a
              href="/amenities"
              onClick={(e) => {
                e.preventDefault();
                trackEvent('internal_link_click', { from: 'lagoon_amenities', to: '/amenities' });
                navigate('/amenities');
              }}
              className="text-brand-teal hover:text-brand-teal/80 underline text-sm font-medium transition-colors inline-block"
            >
              Explore all amenities →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
